import { BRAIN, STATE } from '../actors/ai.js';
import { DRIVE } from './Driving.js';
import { emitCrime } from './Population.js';

/**
 * @file THE CARJACK — and, in the same machine, every ordinary way in and out
 * of a car.
 *
 * F used to be one function call: `Driving.toggle()`, in and out on the same
 * frame. This is the animation that was always meant to go in the middle of
 * it, and it turned out that the signature GTA interaction and "get into a
 * parked car" are the SAME sequence with one phase inserted:
 *
 * ```
 *  SETTLE ──▶ DOOR ──▶ DRAG ──▶ SCRAMBLE ──▶ TRANSFER ──▶ CLOSE
 *  (jack)             (occupied only)         (Driving's two-phase seam)
 * ```
 *
 * Plain entry skips SETTLE, DRAG and SCRAMBLE and is 0.80 s; a carjack runs
 * the lot and is 1.55 s plus however long the target takes to stop; leaving a
 * car runs DOOR → TRANSFER → CLOSE the other way round and is 0.75 s. ONE
 * machine, one table of timings ({@link JACK}), one set of tests.
 *
 * ...and ONE exception, which is the whole of leaving a car at speed: above
 * {@link DRIVE.exitSpeed} F is a BAIL and there is no sequence at all. No door
 * tween, no CLOSE — the player is out on the step they asked, and the door is
 * left hanging open on a car that rolls away without them (`Driving` coasts it
 * to a stop and parks it). The speed gate that used to REFUSE this is gone,
 * owner decision, `HANDOFF.md` #5; the number survives as the line between the
 * two acts rather than as a veto.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO THREE.JS, AND NO STATE OF ITS OWN THAT THE WORLD CANNOT SEE
 * ─────────────────────────────────────────────────────────────────────────────
 * Same deal as `traffic/Traffic.js` and `systems/Driving.js`: the fleet is
 * reached through the director, the victim through `EnemyManager#placeSeated`,
 * the player through its own kinematic hook. That is what lets
 * `tests/carjack.test.mjs` drive the whole sequence — the handshake, the drag
 * path, the crime, every abort — in node with no GPU and a duck-typed fleet.
 *
 * The machine OWNS nothing permanently. Every step of it is a borrow that has
 * a matching return, and `abort()` is the proof: it is called on the player's
 * death and it has to leave a world that is exactly as consistent as one where
 * F was never pressed.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FIVE THINGS THAT WILL BITE
 * ─────────────────────────────────────────────────────────────────────────────
 * • **The victim stays `BRAIN.SEATED` for the whole drag.** That is not a
 *   shortcut, it is the point: SEATED means "somebody else writes my
 *   transform", which is precisely what being dragged is. Nothing new goes in
 *   the `STATE` enum, nothing new goes in `BRAIN`, and the manager needs no
 *   idea that carjacking exists. He becomes a `CIVILIAN` (who runs) or a
 *   `NONE` fighter (who does not) at the instant he lands, and not before.
 *
 * • **He faces the CAR while moving away from it.** Pinning the facing at the
 *   thing that has hold of you is what sells "dragged out" rather than "walked
 *   out", and it costs nothing — the sprite mirror is already a function of
 *   the facing byte.
 *
 * • **The player uses the door they are STANDING at; the driver always comes
 *   out of the driver's.** Both doors fly open at once when the player
 *   approaches from the passenger side. The alternative — walking the player
 *   round the nose — was tried and rejected: at the game's 52-degree pitch a
 *   half-second glide around a bonnet reads as clipping through the car, where
 *   two doors banging open reads as violence, which is the correct reading of
 *   a carjacking.
 *
 * • **A jacked car has to BECOME a parked slot** (`Traffic#takeDriving`)
 *   before `Driving#enterCar` will look at it. Anything else is a second
 *   handover path, and two of those is how the player's car ends up existing
 *   twice.
 *
 * • **The target is PINNED, not merely stopped.** `requestStop` keeps it
 *   still; `pin` keeps the despawn ring off it. A sequence is long enough for
 *   the focus to move, and a car that evaporates mid-drag leaves a man in the
 *   air.
 *
 * @module engine/systems/Carjack
 */

/** @typedef {import('../traffic/Traffic.js').Traffic} Traffic */
/** @typedef {import('../actors/EnemyManager.js').EnemyManager} EnemyManager */

/**
 * Where the machine is. `IDLE` is 0 so a falsy check reads as "nothing is
 * happening"; the rest are the phase order for both directions.
 * @enum {number}
 */
export const JACK_PHASE = Object.freeze({
  IDLE: 0,
  /** Braking a moving target to a standstill. Carjack only, and variable. */
  SETTLE: 1,
  /** The door (or doors) swing open. */
  DOOR: 2,
  /** The driver comes out; a passenger bails alongside. Occupied cars only. */
  DRAG: 3,
  /** He gets up, and the consequences land. Occupied cars only. */
  SCRAMBLE: 4,
  /** `Driving`'s two-phase seam runs: in, or out. */
  TRANSFER: 5,
  /** The door swings shut. */
  CLOSE: 6,
});

/** Debug labels, indexed by {@link JACK_PHASE}. */
export const JACK_PHASE_NAMES = Object.freeze([
  'idle', 'settle', 'door', 'drag', 'scramble', 'transfer', 'close',
]);

/**
 * Every number the sequence owns. Seconds and metres. The three totals they
 * add up to are asserted in `tests/carjack.test.mjs`, so a nudge here that
 * breaks the pacing cannot land quietly.
 */
export const JACK = Object.freeze({
  /** How far F reaches for a DRIVING car's front door. */
  reach: 2.5,
  /**
   * Above this the car is going too fast to grab and F does NOTHING — no
   * animation, no stop request, no feedback. A grab that succeeds at 8 m/s
   * would have to teleport the player onto the running board.
   */
  grabSpeed: 3,
  /** ...and the phase-wait ends once the held car is below this. */
  stopSpeed: 0.3,
  /**
   * Seconds the grab will wait for a held car before giving up and putting
   * everything back. A car wedged nose-first in a junction it has not been
   * granted can take a while, and a player left frozen forever is worse than a
   * carjack that visibly did not take.
   */
  settleMax: 3,

  // ── the fixed phases ──
  door: 0.30,
  drag: 0.60,
  scramble: 0.15,
  /** Handed to `Driving#enterCar` as its two-phase delay. */
  enter: 0.25,
  /** ...and to `exitCar`. */
  exit: 0.20,
  close: 0.25,

  // ── the drag path ──
  /** Where the victim lands: metres LATERALLY clear of the door he came out of. */
  dumpLateral: 1.6,
  /** ...and how far back along the car, so he does not land under a wheel. */
  dumpBack: -0.55,
  /** Peak of the ballistic lift over the arc, metres. Small: he is yanked
   *  off balance, not thrown over the roof. */
  dumpArc: 0.42,
  /** Share of the drag spent getting from the seat to the sill; the rest is
   *  the tumble out. Front-loaded, because the yank is the fast part. */
  sillAt: 0.4,

  // ── the passenger's hurried exit ──
  /** Starts with the doors, ends inside the drag — no arc, he simply gets out. */
  bail: 0.50,
  bailLateral: 1.9,

  /** How far out from the sill the player stands before they are hidden. */
  sillOut: 0.35,
  /** A carjack frightens the street this far. */
  alarmRadius: 16,
});

/** `CarFleet` door name per seat index (1 front passenger, 2/3 rear pair). */
const DOOR_OF_SEAT = Object.freeze(['fl', 'fr', 'rl', 'rr']);

/** @param {number} t @returns {number} Smoothstep on 0..1. */
function smooth(t) {
  const u = t < 0 ? 0 : t > 1 ? 1 : t;
  return u * u * (3 - 2 * u);
}

/** @param {number} v @returns {number} */
function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** @param {number} a @param {number} b @param {number} t */
function lerp(a, b, t) {
  return a + (b - a) * t;
}

/**
 * The vehicle sequence. Owns the F key and nothing else.
 *
 * @param {Object} o
 * @param {Traffic} o.traffic
 * @param {ReturnType<import('./Driving.js').createDriving>} o.driving
 * @param {any} o.player {@link import('../actors/Player.js').Player}
 * @param {EnemyManager|null} [o.manager] Without one there are never occupants
 *   and this is the plain enter/exit machine.
 * @param {any} [o.population] `createPopulation`'s system — the occupant seams.
 * @param {import('./Population.js').FactionRules|null} [o.factions] Provocation.
 * @param {any} [o.bus] For the `'crime'` channel.
 * @param {any} [o.collision] `CollisionGrid` — the dump point is resolved
 *   against it, so nobody is thrown into a wall.
 * @param {(x: number, z: number) => number} [o.heightAt]
 * @returns {Object}
 */
export function createCarjack(o) {
  const {
    traffic,
    driving,
    manager = null,
    population = null,
    factions = null,
    bus = null,
    collision = null,
    heightAt = () => 0,
  } = o ?? {};
  // Rebindable on purpose: Gameplay hot-swaps the Player once its sprite
  // decodes (same reasoning as Driving.js#setPlayer).
  let player = o?.player;

  if (!traffic || !driving || !player) {
    throw new Error('createCarjack: traffic, driving and player are required');
  }

  // ── the machine ───────────────────────────────────────────────────────────
  let phase = JACK_PHASE.IDLE;
  /** `'enter'` (into a car) or `'exit'` (out of one). */
  let mode = 'enter';
  let timer = 0;
  let elapsed = 0;
  let disposed = false;
  let jacks = 0;

  /** DRIVING slot being taken, or -1 (a parked car, or after the handover). */
  let car = -1;
  /** PARKED slot: the target of a plain entry, or where a jacked car landed. */
  let parked = -1;
  /** Fleet handle of whichever car is being animated. Survives the handover. */
  let handle = -1;
  /** Which front door the PLAYER uses: 0 driver, 1 passenger. */
  let side = 0;
  /** Manager slot of the driver being dragged, or -1. */
  let victim = -1;
  /**
   * Every PASSENGER bailing out — front seat and rear pair alike, each with
   * its seat index and its own bail path. Fixed pool, compacted in place, so
   * the per-step machine still allocates nothing.
   * @type {Array<{slot: number, seat: number, a: {x: number, y: number, z: number},
   *   c: {x: number, y: number, z: number}}>}
   */
  const passengers = Array.from({ length: 3 }, () => ({
    slot: -1,
    seat: 0,
    a: { x: 0, y: 0, z: 0 },
    c: { x: 0, y: 0, z: 0 },
  }));
  /** How many of {@link passengers} are live this sequence. */
  let passN = 0;
  /** True once the drag has actually started — decides how `abort` unwinds. */
  let dragged = false;
  /** True when WE called `requestStop` (so only we may release it). */
  let heldByUs = false;
  /** The `'carjack'` crime fires exactly once per sequence. */
  let crimeFired = false;

  // ── scratch (allocated ONCE — a per-step machine must not litter) ──────────
  const from = { x: 0, z: 0 };
  const sill = { x: 0, z: 0 };
  /** Drag path: seat, sill, dump. */
  const dragA = { x: 0, y: 0, z: 0 };
  const dragB = { x: 0, y: 0, z: 0 };
  const dragC = { x: 0, y: 0, z: 0 };
  /** The car's centre when the drag was planned — the victim's facing target. */
  const anchor = { x: 0, z: 0 };
  const world = { x: 0, y: 0, z: 0, yaw: 0 };
  const world2 = { x: 0, y: 0, z: 0, yaw: 0 };
  const local = { x: 0, z: 0 };
  const probe = { x: 0, z: 0 };
  const pick = { side: 0, dist: 0 };
  /** Door names opened this run, in `CarFleet` spelling. */
  const openDoors = [];
  let walkT = 0;
  let walkTotal = 0;

  // ── helpers ───────────────────────────────────────────────────────────────

  /**
   * @param {number} slot
   * @returns {boolean} Still a live SEATED occupant worth animating. DYING
   *   counts as gone: a slot in its death shrink is still `isLive`, and
   *   dragging a body through one is both macabre and wrong — the sequence is
   *   supposed to degrade to a plain entry the moment its victim is shot.
   */
  const seated = (slot) => slot >= 0 && !!manager && manager.isLive(slot)
    && manager.state[slot] !== STATE.DYING && manager.brain[slot] === BRAIN.SEATED;

  /** @param {number} t 0 shut .. 1 open. */
  const setDoors = (t) => {
    const fleet = traffic.fleet;
    if (!fleet || handle < 0) return;
    for (let i = 0; i < openDoors.length; i++) fleet.setDoor(handle, openDoors[i], t);
  };

  /** @param {string} name */
  const wantDoor = (name) => {
    if (name && !openDoors.includes(name)) openDoors.push(name);
  };

  /**
   * Walks the player toward the door sill. Runs across DOOR → DRAG → SCRAMBLE
   * → TRANSFER as one continuous slide rather than per phase, so the approach
   * reads as one movement and the player is still stepping in as the driver
   * comes out past them — which is the whole shot.
   * @param {number} dt
   */
  const walk = (dt) => {
    if (mode !== 'enter' || walkTotal <= 0 || driving.inCar) return;
    walkT = Math.min(walkTotal, walkT + dt);
    const u = smooth(walkT / walkTotal);
    player.moveTo(lerp(from.x, sill.x, u), lerp(from.z, sill.z, u));
  };

  /**
   * Turns a car-local point into world space. `Ry(yaw)` maps +X to
   * `(cos, −sin)` and +Z to `(sin, cos)` — the vehicle stack's convention.
   * @param {number} cx @param {number} cz @param {number} yaw
   * @param {number} lx @param {number} lz
   * @param {{x: number, y: number, z: number}} out
   */
  const toWorld = (cx, cz, yaw, lx, lz, out) => {
    const c = Math.cos(yaw);
    const sn = Math.sin(yaw);
    out.x = cx + lx * c + lz * sn;
    out.z = cz - lx * sn + lz * c;
    return out;
  };

  /**
   * Plans the drag and the bail from the car's CURRENT pose. Called once, at
   * the moment the doors finish opening — by then the target is stopped, so
   * the path is fixed and nothing has to chase a moving anchor.
   */
  const planDrag = () => {
    const arch = traffic.arch[car];
    const yaw = traffic.yaw[car];
    anchor.x = traffic.x[car];
    anchor.z = traffic.z[car];

    if (victim >= 0) {
      const seat = traffic.getSeatWorld(car, 0, world);
      dragA.x = seat.x;
      dragA.y = seat.y;
      dragA.z = seat.z;
      const door = traffic.getDoorWorld(car, 0, world2);
      dragB.x = door.x;
      dragB.z = door.z;
      // The dump: further out along the driver's side (−Z is car-local left)
      // and a little back, so he lands beside the car and not under a wheel.
      traffic.doorLocal(arch, 0, local);
      toWorld(anchor.x, anchor.z, yaw, local.x + JACK.dumpBack, local.z - JACK.dumpLateral, dragC);
      resolveDump(dragC, victim);
      dragC.y = heightAt(dragC.x, dragC.z);
    }

    for (let p = 0; p < passN; p++) {
      const pass = passengers[p];
      const seat = traffic.getSeatWorld(car, pass.seat, world);
      pass.a.x = seat.x;
      pass.a.y = seat.y;
      pass.a.z = seat.z;
      // Out of the door on the passenger's OWN side: the front passenger uses
      // his door anchor; a rear passenger mirrors it at his seat's fore-aft
      // station (the rear door is right beside the rear seat).
      const sideSign = pass.seat % 2 === 0 ? -1 : 1;
      traffic.doorLocal(arch, sideSign < 0 ? 0 : 1, local);
      if (pass.seat >= 2) {
        traffic.seatLocal(arch, pass.seat, probe);
        local.x = probe.x;
      }
      toWorld(anchor.x, anchor.z, yaw, local.x, local.z + sideSign * JACK.bailLateral, pass.c);
      resolveDump(pass.c, pass.slot);
      pass.c.y = heightAt(pass.c.x, pass.c.z);
    }
  };

  /**
   * Pushes a landing point out of the world. Once, at plan time — a body
   * thrown through a shopfront is the sort of thing a player screenshots.
   * @param {{x: number, z: number}} pt @param {number} slot
   */
  const resolveDump = (pt, slot) => {
    if (!collision || typeof collision.resolveCircle !== 'function') return;
    probe.x = pt.x;
    probe.z = pt.z;
    const r = manager?.radius?.[slot] ?? 0.35;
    const res = collision.resolveCircle(probe, r, 2);
    pt.x = res.x;
    pt.z = res.z;
  };

  /**
   * The victim, `u` of the way out. Piecewise seat → sill → dump with a
   * ballistic lift over the whole thing, and the facing pinned at the car.
   * @param {number} u 0..1
   */
  const moveVictim = (u) => {
    if (victim < 0 || !manager) return;
    const s = smooth(u);
    let x;
    let z;
    if (s < JACK.sillAt) {
      const k = s / JACK.sillAt;
      x = lerp(dragA.x, dragB.x, k);
      z = lerp(dragA.z, dragB.z, k);
    } else {
      const k = (s - JACK.sillAt) / (1 - JACK.sillAt);
      x = lerp(dragB.x, dragC.x, k);
      z = lerp(dragB.z, dragC.z, k);
    }
    const y = lerp(dragA.y, dragC.y, s) + Math.sin(Math.PI * clamp01(u)) * JACK.dumpArc;
    // Facing the thing that has hold of him while his position runs the other
    // way. That is the entire tell that he is being dragged, and it is free.
    //
    // ⚠ `inCabin: false` (the sixth argument): nobody inside a car is drawn,
    // so this is what puts him back ON SCREEN the instant the drag starts. He
    // is still `BRAIN.SEATED` — the difference is where his body is, not whose
    // it is.
    manager.placeSeated(victim, x, y, z, Math.atan2(anchor.x - x, anchor.z - z), false);
  };

  /**
   * The passengers, `u` of the way out. A straight hurried exit — no arc, and
   * facing where they are GOING, because nobody has hold of them.
   * @param {number} u 0..1
   */
  const moveBail = (u) => {
    if (passN === 0 || !manager) return;
    const s = smooth(clamp01(u));
    for (let p = 0; p < passN; p++) {
      const pass = passengers[p];
      if (pass.slot < 0) continue;
      const x = lerp(pass.a.x, pass.c.x, s);
      const z = lerp(pass.a.z, pass.c.z, s);
      const y = lerp(pass.a.y, pass.c.y, s);
      // Out of the cabin, same as the victim: on screen and drawn as himself.
      manager.placeSeated(pass.slot, x, y, z, Math.atan2(x - anchor.x, z - anchor.z), false);
    }
  };

  /**
   * Both occupants are on the tarmac. THE CONSEQUENCES, and the only place
   * they fire:
   *  • a civilian becomes a `CIVILIAN` brain and is scared into a run;
   *  • a gang member becomes an ordinary `NONE` fighter and his crew is
   *    provoked — he engages out of the targeting that already exists, with
   *    no script and no special case;
   *  • the `'carjack'` crime goes out ONCE, through `Population#emitCrime` so
   *    it carries the same payload as every other crime;
   *  • and the street hears it.
   */
  const land = () => {
    if (!manager) return;
    // Put them exactly on their end points first: the last animated step is a
    // fraction short, and a body that settles 4 cm in the air is visible.
    moveVictim(1);
    moveBail(1);

    // EVERY passenger flees — front seat and rear pair alike.
    for (let p = 0; p < passN; p++) {
      const slot = passengers[p].slot;
      if (!seated(slot)) continue;
      manager.setBrain(slot, BRAIN.CIVILIAN);
      population?.adopt(slot);
      manager.scare(slot, anchor.x, anchor.z);
    }

    if (seated(victim)) {
      const team = manager.team[victim];
      const gang = !!factions?.GANGS?.includes(team);
      // Who gets up FIGHTING: a crew member (always — the drag is the
      // provocation) and anyone already hostile to the player, which is how a
      // policeman hauled out of his own cruiser draws instead of fleeing his
      // own arrest. Everyone else runs. Only the gang branch provokes, because
      // the police half is the wanted level's to decide, not a carjack's.
      const fights = gang || !!factions?.state?.isPlayerHostile?.(team);
      manager.setBrain(victim, fights ? BRAIN.NONE : BRAIN.CIVILIAN);
      population?.adopt(victim);
      if (gang) factions?.state?.provoke(team);
      if (!fights) manager.scare(victim, anchor.x, anchor.z);
      if (!crimeFired) {
        crimeFired = true;
        jacks++;
        emitCrime(bus, manager, 'carjack', victim);
      }
      population?.alarm(manager.x[victim], manager.z[victim], JACK.alarmRadius);
    }

    victim = -1;
    passN = 0;
  };

  /** Puts released occupants back where they were sitting. */
  const reseatAll = () => {
    if (car < 0 || !population) return;
    population.reseat(car, 0, victim);
    for (let p = 0; p < passN; p++) population.reseat(car, passengers[p].seat, passengers[p].slot);
    victim = -1;
    passN = 0;
  };

  /** Everything back to rest. Does NOT touch the world. */
  const finish = () => {
    phase = JACK_PHASE.IDLE;
    mode = 'enter';
    timer = 0;
    elapsed = 0;
    car = -1;
    parked = -1;
    handle = -1;
    victim = -1;
    passN = 0;
    dragged = false;
    heldByUs = false;
    crimeFired = false;
    walkT = 0;
    walkTotal = 0;
    openDoors.length = 0;
  };

  /**
   * Gives up mid-sequence and leaves a consistent world. Called on the
   * player's death, on the target evaporating, and when a phase-wait times
   * out.
   *
   * The unwind depends on ONE thing: has the drag started?
   *  • Not yet — nobody has left their seat, so put them back and hand the car
   *    to the director exactly as it was.
   *  • Already — the driver is out (or half out) and there is no honest way to
   *    put him back through a door he has been pulled through. Finish his half
   *    of the sequence, bill the crime, and leave the car stopped and
   *    driverless, which is a state the world already knows how to hold
   *    (`Population#setDriverless`) and which the player can come back to.
   */
  const abort = () => {
    if (car >= 0) {
      traffic.pin(car, false);
      if (dragged) {
        land();
        population?.setDriverless(car);
      } else {
        if (heldByUs) traffic.releaseStop(car);
        reseatAll();
      }
    }
    setDoors(0);
    finish();
  };

  // ── beginnings ────────────────────────────────────────────────────────────

  /**
   * Enters the DOOR phase. Split out because a carjack reaches it two ways —
   * straight away when the target is already stopped, or out of SETTLE.
   * @returns {boolean} False when the car has gone; the caller unwinds.
   */
  const beginDoor = () => {
    // The sill and the approach are captured HERE, not at the key press: a
    // target that had to be braked has rolled several metres since.
    const w = car >= 0
      ? traffic.getDoorWorld(car, side, world)
      : traffic.getParkedDoorWorld(parked, world);
    if (!w) return false;
    // Stand just outside the sill. The player is hidden the moment they are
    // in, so this only has to look like the door, not like a seat.
    const away = Math.atan2(w.x - (car >= 0 ? traffic.x[car] : traffic.pX[parked]),
      w.z - (car >= 0 ? traffic.z[car] : traffic.pZ[parked]));
    sill.x = w.x + Math.sin(away) * JACK.sillOut;
    sill.z = w.z + Math.cos(away) * JACK.sillOut;
    from.x = player.position.x;
    from.z = player.position.z;
    walkT = 0;
    walkTotal = JACK.door + JACK.enter
      + (victim >= 0 || passN > 0 ? JACK.drag + JACK.scramble : 0);
    phase = JACK_PHASE.DOOR;
    timer = JACK.door;
    setDoors(0);
    return true;
  };

  /**
   * @returns {boolean} Too fast to step out of calmly, so F is a BAIL. The
   *   gate that used to refuse at this speed is gone (`HANDOFF.md` #5).
   */
  const bailing = () => Math.abs(driving.body.speed) > DRIVE.exitSpeed;

  /**
   * OUT, NOW. `Driving#exitCar` with a ZERO delay is the instant transfer, so
   * there is no phase to sit in and the machine is back at IDLE before the
   * caller returns — which is why the CLOSE phase never runs and the door is
   * left at 1.
   *
   * That open door is the point rather than an omission: nobody shut it. It
   * rides the car through `Traffic#returnParked`, which resets the lamps and
   * the steer but deliberately not the doors, so the car parks itself with the
   * door still hanging open and the next honest entry (`beginDoor`'s
   * `setDoors(0)`) is what finally closes it.
   * @returns {boolean}
   */
  const bailOut = () => {
    if (!driving.exitCar(false, 0)) {
      // No car to leave — it went out from under the sequence. The door is the
      // only thing there is to unwind.
      setDoors(0);
      finish();
      return false;
    }
    setDoors(1);
    finish();
    return true;
  };

  /** The handover, in whichever direction. */
  const beginTransfer = () => {
    if (mode === 'exit') {
      // They accelerated while the door was opening. This used to CANCEL the
      // exit (the speed gate refused and the machine put the door back); with
      // the gate gone it CONVERTS — a player who asked to get out and then went
      // faster gets out faster, which is the only reading of those two inputs
      // that does not put them back behind a wheel they let go of.
      if (bailing()) {
        bailOut();
        return;
      }
      if (!driving.exitCar(false, JACK.exit)) {
        // The car went out from under them. Nothing to unwind but the door.
        setDoors(0);
        finish();
        return;
      }
      phase = JACK_PHASE.TRANSFER;
      timer = JACK.exit;
      return;
    }

    if (car >= 0) {
      // Everything the director owned about this car stops here. `releaseStop`
      // is unconditional on purpose: a DRIVERLESS car arrives already held by
      // the population director, and that hold has to go or the byte outlives
      // the slot. `takeDriving` clears it a second time — belt and braces on
      // the one piece of state that would silently freeze the slot's next
      // tenant.
      traffic.releaseStop(car);
      traffic.pin(car, false);
      const slot = traffic.takeDriving(car);
      car = -1;
      heldByUs = false;
      if (slot < 0) {
        abort();
        return;
      }
      parked = slot;
      handle = traffic.parkedHandle(parked);
    }
    if (!driving.enterCar(parked, JACK.enter)) {
      abort();
      return;
    }
    phase = JACK_PHASE.TRANSFER;
    timer = JACK.enter;
  };

  /** @returns {boolean} */
  const beginExit = () => {
    if (!driving.inCar) return false;
    mode = 'exit';
    parked = driving.carSlot;
    handle = driving.fleetHandle;
    car = -1;
    side = 0;
    victim = -1;
    passN = 0;
    dragged = false;
    heldByUs = false;
    crimeFired = false;
    walkT = 0;
    walkTotal = 0;
    openDoors.length = 0;
    elapsed = 0;
    wantDoor('fl');
    // At speed there is no choreography to begin: the bail is the whole of it,
    // and slamming the door shut first would be an animation nobody sees.
    if (bailing()) return bailOut();
    setDoors(0);
    phase = JACK_PHASE.DOOR;
    timer = JACK.door;
    return true;
  };

  /** @returns {boolean} */
  const beginEnter = () => {
    const px = player.position.x;
    const pz = player.position.z;
    const jackSlot = traffic.nearestJackable(px, pz, JACK.reach, pick);
    const parkSlot = traffic.nearestEnterable(px, pz, DRIVE.enterRadius);

    // Nearest door wins. A parked car is measured at its driver door, a
    // driving one at whichever front door the player is standing at.
    let useJack = jackSlot >= 0;
    if (jackSlot >= 0 && parkSlot >= 0) {
      const w = traffic.getParkedDoorWorld(parkSlot, world);
      useJack = !w || pick.dist < Math.hypot(px - w.x, pz - w.z);
    }
    if (!useJack && parkSlot < 0) return false;

    mode = 'enter';
    dragged = false;
    heldByUs = false;
    crimeFired = false;
    victim = -1;
    passN = 0;
    openDoors.length = 0;
    elapsed = 0;

    if (!useJack) {
      car = -1;
      parked = parkSlot;
      side = 0;
      handle = traffic.parkedHandle(parked);
      wantDoor('fl');
      if (!beginDoor()) {
        finish();
        return false;
      }
      return true;
    }

    // Too fast to grab: F does nothing at all. No stop request, no half-played
    // animation — a press that quietly re-plans the traffic is worse feedback
    // than a press that did not register.
    if (traffic.v[jackSlot] > JACK.grabSpeed) return false;

    car = jackSlot;
    parked = -1;
    side = pick.side;
    handle = traffic.drivingHandle(car);
    // The pin goes on FIRST and comes off in exactly two places (the handover
    // and `abort`), so there is no path out of here that leaves one set.
    traffic.pin(car, true);

    // Claim the occupants BEFORE anything else: the director has to stop
    // slaving them to their seats on the very step the sequence takes over, or
    // the first drag frame is fought and lost.
    victim = population ? population.releaseOccupant(car, 0) : -1;
    if (!seated(victim)) victim = -1;
    for (let seat = 1; seat <= 3; seat++) {
      const slot = population ? population.releaseOccupant(car, seat) : -1;
      if (!seated(slot)) continue;
      passengers[passN].slot = slot;
      passengers[passN].seat = seat;
      passN++;
    }

    // The player's door, the driver's door, and one per passenger. They can
    // be the same door — the usual case is all of them being `fl` with nobody
    // aboard. A rear passenger bangs his own rear door open.
    wantDoor(side === 0 ? 'fl' : 'fr');
    if (victim >= 0) wantDoor('fl');
    for (let p = 0; p < passN; p++) wantDoor(DOOR_OF_SEAT[passengers[p].seat]);

    // HOLD IT, even when it is already at a standstill. A car stopped at the
    // back of a junction queue is one grant away from driving off, and the
    // sequence would then be animating a door that has left — which is how a
    // dragged man ends up 12 m behind the car he was dragged out of. The hold
    // is only OURS to release if nobody else had already placed one (the
    // driverless case, which belongs to the population director).
    heldByUs = traffic.hold[car] === 0;
    traffic.requestStop(car);

    if (traffic.v[car] < JACK.stopSpeed) {
      if (!beginDoor()) {
        abort();
        return false;
      }
    } else {
      phase = JACK_PHASE.SETTLE;
      timer = JACK.settleMax;
    }
    return true;
  };

  /**
   * THE F KEY. In beside a car (parked or occupied), out of the one you are
   * driving, and NOTHING while a sequence is running — the whole point of a
   * carjack is that once it starts it happens.
   * @returns {boolean} True when a sequence began.
   */
  const press = () => {
    if (disposed || phase !== JACK_PHASE.IDLE) return false;
    if (player.dead) return false;
    if (driving.inCar) return beginExit();
    if (driving.phase !== 'foot') return false;
    return beginEnter();
  };

  // ── the step ──────────────────────────────────────────────────────────────

  /**
   * One fixed step. Call it BEFORE `Driving#update` so the two-phase seam's
   * timer starts running on the step it was requested.
   * @param {number} dt
   */
  const update = (dt) => {
    if (disposed || phase === JACK_PHASE.IDLE || !(dt > 0)) return;
    elapsed += dt;

    // Taking damage does not interrupt a carjack — that is GTA-authentic and
    // it is what makes jacking a car under fire a decision. Dying does.
    if (player.dead) {
      abort();
      return;
    }
    // The target evaporating is the other one. It cannot happen to a pinned
    // car through the despawn ring, but a level teardown or a scripted retire
    // still can.
    if (car >= 0 && !traffic.isDriving(car)) {
      abort();
      return;
    }
    // Somebody shot the driver mid-sequence. Drop him and carry on: from here
    // this is an empty car and the sequence is a plain entry.
    if (phase <= JACK_PHASE.DRAG) {
      if (victim >= 0 && !seated(victim)) victim = -1;
      let keep = 0;
      for (let p = 0; p < passN; p++) {
        if (!seated(passengers[p].slot)) continue;
        if (keep !== p) {
          const t = passengers[keep];
          passengers[keep] = passengers[p];
          passengers[p] = t;
        }
        keep++;
      }
      passN = keep;
    }

    switch (phase) {
      case JACK_PHASE.SETTLE: {
        timer -= dt;
        if (traffic.v[car] < JACK.stopSpeed) {
          if (!beginDoor()) abort();
        } else if (timer <= 0) {
          abort();
        }
        break;
      }

      case JACK_PHASE.DOOR: {
        timer -= dt;
        setDoors(smooth(1 - clamp01(timer / JACK.door)));
        walk(dt);
        if (timer > 0) break;
        setDoors(1);
        if (mode === 'enter' && (victim >= 0 || passN > 0)) {
          planDrag();
          dragged = true;
          phase = JACK_PHASE.DRAG;
          timer = JACK.drag;
        } else {
          // No drag: the slide-in is all that is left of the approach.
          if (mode === 'enter') walkTotal = walkT + JACK.enter;
          beginTransfer();
        }
        break;
      }

      case JACK_PHASE.DRAG: {
        timer -= dt;
        const t = clamp01(1 - timer / JACK.drag);
        moveVictim(t);
        moveBail((JACK.drag - timer) / JACK.bail);
        walk(dt);
        if (timer <= 0) {
          land();
          phase = JACK_PHASE.SCRAMBLE;
          timer = JACK.scramble;
        }
        break;
      }

      case JACK_PHASE.SCRAMBLE: {
        timer -= dt;
        walk(dt);
        if (timer <= 0) beginTransfer();
        break;
      }

      case JACK_PHASE.TRANSFER: {
        timer -= dt;
        walk(dt);
        // `Driving` owns the clock here; ours is only a floor. Waiting on its
        // STATE rather than on our timer is what keeps the two from drifting
        // apart by a step — and it has to be the settled phase, not `inCar`:
        // `inCar` goes false the instant `exitCar` is REQUESTED, which is the
        // start of the climb-out and not the end of it.
        const done = driving.phase === (mode === 'enter' ? 'car' : 'foot');
        if (done || timer <= -0.5) {
          phase = JACK_PHASE.CLOSE;
          timer = JACK.close;
        }
        break;
      }

      case JACK_PHASE.CLOSE: {
        timer -= dt;
        setDoors(smooth(clamp01(timer / JACK.close)));
        if (timer <= 0) {
          setDoors(0);
          finish();
        }
        break;
      }

      default:
        finish();
        break;
    }
  };

  return {
    name: 'carjack',
    press,
    abort,
    update,
    /** Gameplay's player hot-swap points us at the replacement actor. */
    setPlayer(p) {
      player = p;
    },
    /** @returns {boolean} A sequence is running. */
    get active() {
      return phase !== JACK_PHASE.IDLE;
    },
    /**
     * @returns {boolean} The player's own movement is suspended. True for
     *   every phase up to the handover; once they are driving, WASD is the
     *   car's and the door simply finishes shutting.
     */
    get locked() {
      return phase !== JACK_PHASE.IDLE && !driving.inCar;
    },
    /** @returns {number} {@link JACK_PHASE} */
    get phase() {
      return phase;
    },
    /** @returns {string} */
    get phaseName() {
      return JACK_PHASE_NAMES[phase] ?? 'idle';
    },
    /** @returns {number} Driving slot being jacked, or -1. */
    get target() {
      return car;
    },
    /** @returns {number} Manager slot being dragged, or -1. */
    get victim() {
      return victim;
    },
    /** @returns {{phase: string, mode: string, elapsed: number, jacks: number}} */
    stats() {
      return {
        phase: JACK_PHASE_NAMES[phase] ?? 'idle',
        mode,
        elapsed: Math.round(elapsed * 100) / 100,
        jacks,
      };
    },
    /** Idempotent. Unwinds any sequence in flight. */
    dispose() {
      if (disposed) return;
      abort();
      disposed = true;
    },
  };
}
